// ============================================================================
// Admin-granted brand credit — client
// ============================================================================
// Two halves, split by who is allowed to do what:
//
//   READS  go straight to Postgres through RLS-gated RPCs. Every one of them
//          checks public.is_admin() internally, so an admin session can list
//          brands, its own grant permissions and the grant log without the
//          worker being involved.
//   WRITES go through the Cloudflare worker with the admin's JWT attached. The
//          grant RPC's EXECUTE is service_role-only, so this is the only way in
//          — and the worker resolves WHO is granting from the verified token
//          rather than anything this file could put in a payload.
//
// Nothing here decides whether a grant is allowed. The caps and permission in
// `CreditGrantContext` come from the same SQL function that enforces them
// (admin_credit_grant_context / admin_grant_brand_credit), so the form can't
// offer an amount the database will then refuse. Treat this module's limits as
// display data, never as the gate.
//
// See creator-app/supabase/migrations/20260813000000_admin_roles_and_credit_grants.sql
// ============================================================================

import { supabase } from './supabase'
import { workerAuthedFetch } from './workerApi'

// ── Payment method (transaction platform) ───────────────────────────────────
// Human labels for the Select in both grant and deduct forms. The VALUES here
// must match the DB's CHECK constraint on
// brand_credit_transactions.transaction_platform (both migrations) and the
// worker's TRANSACTION_PLATFORMS — this file doesn't call the DB to fetch the
// allowlist since a fixed set of payment rails doesn't need a round trip,
// but each context response also echoes `transaction_platforms` (the same
// values, unlabeled) so a form can cross-check rather than trust this file
// alone. Add a rail here AND in both migrations' CHECK constraints AND in the
// worker's TRANSACTION_PLATFORMS — three places, kept in sync by hand.
export const TRANSACTION_PLATFORM_OPTIONS: { value: string; label: string }[] = [
  { value: 'whop', label: 'Whop' },
  { value: 'stripe', label: 'Stripe' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'other', label: 'Other' },
  { value: 'bmc_out_of_pocket', label: 'BMC Funding Out of Pocket' },
]

/**
 * The one platform with no external payment to point at — BMC funded this
 * directly rather than money moving through Whop/Stripe/PayPal/bank transfer.
 * admin_grant_brand_credit() (and the CHECK constraint backing it) is the one
 * that actually makes transaction_id optional for this value; this constant
 * just lets the grant forms mirror that rule instead of re-typing the literal
 * in four places.
 */
export const OUT_OF_POCKET_PLATFORM = 'bmc_out_of_pocket'

// ── Types ───────────────────────────────────────────────────────────────────

export interface CreditGrantLimits {
  min_grant_cents: number
  daily_limit_cents: number
  granted_24h_cents: number
  remaining_24h_cents: number
}

export interface CreditGrantAdmin {
  user_id: string
  email: string
  display_name: string | null
  role: string
  role_label: string | null
  is_active: boolean
}

export interface CreditGrantBrand {
  id: string
  brand_name: string | null
  contact_email: string | null
  plan_tier: string | null
  balance_cents: number
  held_cents: number
  available_cents: number
  lifetime_granted_cents: number
  grant_count: number
  last_granted_at: string | null
  /** The acting admin belongs to this brand's team — the DB refuses the grant. */
  is_member_of_brand: boolean
}

export interface CreditGrantContext {
  /** null = this admin is not on the credit roster at all. */
  admin: CreditGrantAdmin | null
  /** app_metadata.is_admin on the auth account, re-checked server-side. */
  is_auth_admin: boolean
  permissions: string[]
  can_grant: boolean
  /** Why the grant form is unavailable. null when can_grant. */
  blocked_reason: string | null
  limits: CreditGrantLimits
  /** Single source of truth for the payment-method dropdown — mirrors the
   *  DB's CHECK constraint so the form can't offer a value the RPC would
   *  then reject. Shared with CreditDeductContext's list (same values). */
  transaction_platforms: string[]
  /** Present only when a brand_id was passed. */
  brand: CreditGrantBrand | null
}

export interface CreditGrantResult {
  grant_id: string
  ledger_id: string
  brand_id: string
  brand_name: string | null
  granted_cents: number
  balance_before_cents: number
  balance_cents: number
  granted_by: string
  granted_24h_cents: number
  daily_limit_cents: number
  transaction_platform: string
  transaction_id: string
  /** true = this exact grant already landed; no new credit was created. */
  replayed: boolean
}

export interface BrandCreditOption {
  brand_id: string
  brand_name: string | null
  contact_email: string | null
  plan_tier: string | null
  balance_cents: number
  held_cents: number
  available_cents: number
  lifetime_granted_cents: number
  last_granted_at: string | null
}

export type CreditGrantStatus = 'applied' | 'denied' | 'failed'

export interface CreditGrantRow {
  id: string
  brand_id: string
  brand_name: string | null
  amount_cents: number
  reason: string
  /** Mandatory on a grant — validated by admin_grant_brand_credit(). */
  transaction_platform: string | null
  transaction_id: string | null
  internal_note: string | null
  // Column names match brand_credit_transactions exactly (shared table with
  // deductions — see fetchCreditGrantLog's direction='grant' filter).
  admin_user_id: string | null
  admin_email: string
  admin_role: string | null
  status: CreditGrantStatus
  error_message: string | null
  ledger_entry_id: string | null
  idempotency_key: string
  balance_before_cents: number | null
  balance_after_cents: number | null
  request_ip: string | null
  user_agent: string | null
  metadata: Record<string, unknown> | null
  reversed_at: string | null
  reversed_by: string | null
  created_at: string
}

export interface CreditGrantStats {
  window_days: number
  total_granted_cents: number
  total_grants: number
  brands_credited: number
  window_granted_cents: number
  window_grants: number
  /** Refused attempts in the window — a run of these is worth looking at. */
  window_refused: number
  reversed_cents: number
}

// ── Reads ───────────────────────────────────────────────────────────────────

/**
 * What this admin may do, and (with a brand) that brand's live balance.
 * Resolves the actor from the session's JWT server-side — there is no way to ask
 * about somebody else's permissions.
 */
export async function fetchCreditGrantContext(brandId?: string | null): Promise<CreditGrantContext> {
  const { data, error } = await supabase.rpc('admin_my_credit_grant_context', {
    p_brand_id: brandId ?? null,
  })
  if (error) throw error
  return data as CreditGrantContext
}

/** Brand picker. Returns only the columns the grant form shows. */
export async function searchCreditBrands(search: string, limit = 20): Promise<BrandCreditOption[]> {
  const { data, error } = await supabase.rpc('admin_credit_brand_search', {
    p_search: search.trim() || null,
    p_limit: limit,
  })
  if (error) throw error
  return (data ?? []) as BrandCreditOption[]
}

export async function fetchCreditGrantStats(days = 30): Promise<CreditGrantStats> {
  const { data, error } = await supabase.rpc('admin_credit_grant_stats', { p_days: days })
  if (error) throw error
  return data as CreditGrantStats
}

export interface CreditGrantLogParams {
  page: number
  pageSize: number
  search?: string
  status?: CreditGrantStatus | 'all'
  brandId?: string | null
  adminEmail?: string | null
}

/**
 * The credit log, newest first, with an exact total for pagination.
 * brand_name is denormalised onto the row, so this needs no join and no
 * brand_configs read.
 */
export async function fetchCreditGrantLog(
  params: CreditGrantLogParams
): Promise<{ rows: CreditGrantRow[]; total: number }> {
  const from = (params.page - 1) * params.pageSize
  const to = from + params.pageSize - 1

  // Shared table with deductions (brand_credit_transactions) — direction is
  // what scopes this to grants only.
  let query = supabase
    .from('brand_credit_transactions')
    .select('*', { count: 'exact' })
    .eq('direction', 'grant')
    .order('created_at', { ascending: false })
    .range(from, to)

  if (params.status && params.status !== 'all') query = query.eq('status', params.status)
  if (params.brandId) query = query.eq('brand_id', params.brandId)
  if (params.adminEmail) query = query.eq('admin_email', params.adminEmail)

  const search = params.search?.trim()
  if (search) {
    // Brand name, granting admin, or the reason text — the three things anyone
    // actually remembers about a grant they're trying to find again.
    const escaped = search.replace(/[%,()]/g, '')
    query = query.or(
      `brand_name.ilike.%${escaped}%,admin_email.ilike.%${escaped}%,reason.ilike.%${escaped}%`
    )
  }

  const { data, error, count } = await query
  if (error) throw error
  return { rows: (data ?? []) as CreditGrantRow[], total: count ?? 0 }
}

/** Distinct granting admins, for the log's "granted by" filter. */
export async function fetchGrantingAdmins(): Promise<string[]> {
  const { data, error } = await supabase
    .from('brand_credit_transactions')
    .select('admin_email')
    .eq('direction', 'grant')
    .order('admin_email')
  if (error) throw error
  return Array.from(new Set((data ?? []).map((r: any) => r.admin_email).filter(Boolean)))
}

// ── Brand-purchased credit (top-ups) ────────────────────────────────────────
// Read-only here, and deliberately so. A top-up is not an admin action: it is
// created by the brand and completed by the Whop webhook, and no admin has a
// button that moves it. Ops needs to SEE it — a brand asking "where did my
// $5,000 go" is answered by this table, and a reversal shortfall is the number
// finance has to chase — but the write path stays out of the admin app entirely.

export type TopupStatus = 'pending' | 'processing' | 'credited' | 'failed' | 'reversed' | 'abandoned'

export interface CreditTopupRow {
  id: string
  brand_id: string
  brand_name: string | null
  initiated_by_email: string | null
  whop_plan_id: string
  expected_amount_cents: number
  /** True = the brand typed this amount rather than picking a listed tier.
   *  Snapshotted at purchase, so a later reprice can't rewrite history. */
  is_custom_amount: boolean
  settled_amount_cents: number | null
  credited_cents: number | null
  /** The Whop promo code applied, if any. Non-null explains a row where
   *  credited_cents exceeds settled_amount_cents — a BMC-issued code credits
   *  the full tier price and charges the discounted amount. */
  promo_code: string | null
  /** How much of credited_cents the promo code covered rather than the card. */
  promo_discount_cents: number | null
  currency: string | null
  status: TopupStatus
  /** Rail the money came down: card | ach | crypto | Whop's raw value. */
  payment_method: string | null
  /** Human-readable extra — card brand + last four, or bank name. */
  payment_method_detail: string | null
  whop_payment_id: string | null
  whop_checkout_id: string | null
  ledger_entry_id: string | null
  reversal_reason: string | null
  reversed_at: string | null
  /** Credit already spent when the payment was clawed back — unrecoverable.
   *  Non-zero means BMC is out of pocket on this row. */
  reversal_shortfall_cents: number | null
  error_message: string | null
  /** What Whop said about this payment the last time the worker asked, stored
   *  verbatim (creator-app 20261005000000). Null on rows nothing has re-checked.
   *  Diagnostic only — nothing reads it to decide money. The row that prompted
   *  it: a $10,000 wire reading 'Payment processing' here for three days while
   *  Whop's own dashboard showed the payment as Incomplete. */
  last_whop_status: string | null
  last_whop_substatus: string | null
  created_at: string
  credited_at: string | null
}

/** How long a top-up may sit in 'processing' before it stops being routine.
 *  Mirrors TOPUP_PROCESSING_EXPECTED_DAYS in the worker and the brand wallet —
 *  ACH is 3-5 BUSINESS days, a wire about one, so seven clears both. Past it
 *  nothing is failed; it is only flagged, because Whop says of this state both
 *  "may still fail" and "may still settle". */
export const TOPUP_PROCESSING_EXPECTED_DAYS = 7

export function topupAgeDays(createdAt: string, now = Date.now()): number {
  return (now - new Date(createdAt).getTime()) / 86_400_000
}

export const TOPUP_STATUS_LABEL: Record<TopupStatus, string> = {
  pending:   'Awaiting payment',
  // Authorised, money in flight, no credit yet. DELIBERATELY RAIL-NEUTRAL:
  // Whop does not expose the payment method until a payment settles (a
  // processing row's payment_method is always null, while a credited one names
  // the card), so calling this a bank transfer was a guess — and a wrong one
  // for every card that sat in Whop's pending state. The table upgrades the
  // wording to "Bank transfer clearing" only where payment_method actually
  // says 'ach'.
  processing: 'Payment processing',
  credited:  'Credited',
  failed:    'Payment failed',
  reversed:  'Refunded / charged back',
  abandoned: 'Abandoned',
}

export interface CreditTopupLogParams {
  page: number
  pageSize: number
  status?: TopupStatus | 'all';
  brandId?: string | null
  search?: string
  /**
   * Hide checkouts that were opened and never paid. On by default, because
   * opening the tier picker writes a row and browsing between tiers used to
   * write several — so 'abandoned' is provably-not-a-purchase noise that would
   * otherwise bury the real ones. Turned off by the "Abandoned" filter.
   */
  hideAbandoned?: boolean
}

export async function fetchCreditTopupLog(
  params: CreditTopupLogParams
): Promise<{ rows: CreditTopupRow[]; total: number }> {
  const from = (params.page - 1) * params.pageSize
  const to = from + params.pageSize - 1

  let query = supabase
    .from('brand_credit_topups')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(from, to)

  if (params.status && params.status !== 'all') query = query.eq('status', params.status)
  // Only meaningful when no explicit status was chosen — picking "Abandoned"
  // must not then filter abandoned rows back out.
  else if (params.hideAbandoned) query = query.neq('status', 'abandoned')
  if (params.brandId) query = query.eq('brand_id', params.brandId)

  const search = params.search?.trim()
  if (search) {
    // Brand, the team member who bought it, or the Whop payment id — the three
    // handles anyone has when chasing a specific purchase.
    const escaped = search.replace(/[%,()]/g, '')
    query = query.or(
      `brand_name.ilike.%${escaped}%,initiated_by_email.ilike.%${escaped}%,whop_payment_id.ilike.%${escaped}%`
    )
  }

  const { data, error, count } = await query
  if (error) throw error
  return { rows: (data ?? []) as CreditTopupRow[], total: count ?? 0 }
}

/** Headline numbers for the top-up panel over a rolling window.
 *
 *  `shortfallCents` is the one that matters operationally: credit that was
 *  bought, spent, and then charged back, which no automated path can recover. */
export async function fetchCreditTopupStats(days = 30): Promise<{
  /** Credit ISSUED — what landed in brand wallets. Includes credit a promo code
   *  paid for, so this is not revenue. */
  creditedCents: number
  creditedCount: number
  /** Money actually COLLECTED on credited purchases. Diverges from
   *  creditedCents once promo codes are in use, which is exactly why the two
   *  are reported separately: one is a liability we took on, the other is cash. */
  collectedCents: number
  /** Credit given away by promo codes — creditedCents minus collectedCents,
   *  summed from the recorded discounts rather than derived, so a partial
   *  discount and a full comp are both counted the same way. */
  compedCents: number
  compedCount: number
  pendingCount: number
  /** Authorised and clearing — ACH in flight. Money we expect but do not have. */
  processingCount: number
  processingCents: number
  failedCount: number
  reversedCount: number
  shortfallCents: number
}> {
  const since = new Date(Date.now() - days * 86_400_000).toISOString()
  const { data, error } = await supabase
    .from('brand_credit_topups')
    .select('status,credited_cents,settled_amount_cents,promo_discount_cents,expected_amount_cents,reversal_shortfall_cents')
    .gte('created_at', since)
  if (error) throw error

  const rows = (data ?? []) as Pick<
    CreditTopupRow,
    'status' | 'credited_cents' | 'settled_amount_cents' | 'promo_discount_cents'
    | 'expected_amount_cents' | 'reversal_shortfall_cents'
  >[]
  const processing = rows.filter(r => r.status === 'processing')
  const credited = rows.filter(r => r.status === 'credited')
  const comped = credited.filter(r => (r.promo_discount_cents ?? 0) > 0)
  return {
    // Reversed rows are excluded from revenue: that money went back.
    creditedCents:  credited.reduce((s, r) => s + (r.credited_cents ?? 0), 0),
    creditedCount:  credited.length,
    collectedCents: credited.reduce((s, r) => s + (r.settled_amount_cents ?? 0), 0),
    compedCents:    comped.reduce((s, r) => s + (r.promo_discount_cents ?? 0), 0),
    compedCount:    comped.length,
    pendingCount:   rows.filter(r => r.status === 'pending').length,
    processingCount: processing.length,
    // Expected, not credited — by definition nothing has settled on these yet.
    processingCents: processing.reduce((s, r) => s + r.expected_amount_cents, 0),
    failedCount:    rows.filter(r => r.status === 'failed').length,
    reversedCount:  rows.filter(r => r.status === 'reversed').length,
    shortfallCents: rows.reduce((s, r) => s + (r.reversal_shortfall_cents ?? 0), 0),
  }
}

export interface AdminRosterRow {
  user_id: string
  email: string
  display_name: string | null
  role: string
  permissions: string[]
  is_active: boolean
  daily_grant_limit_cents: number | null
  notes: string | null
  created_at: string
}

/**
 * Who holds credit permissions. Read-only by design — admin_users has no write
 * policy, so this roster can only be changed from the SQL editor.
 */
export async function fetchAdminRoster(): Promise<AdminRosterRow[]> {
  const { data, error } = await supabase
    .from('admin_users')
    .select('user_id,email,display_name,role,permissions,is_active,daily_grant_limit_cents,notes,created_at')
    .order('is_active', { ascending: false })
    .order('email')
  if (error) throw error
  return (data ?? []) as AdminRosterRow[]
}

// ── Write ───────────────────────────────────────────────────────────────────

export interface GrantBrandCreditInput {
  brandId: string
  amountCents: number
  /** Shown to the brand on their wallet history. Mandatory, min 3 chars. */
  reason: string
  /**
   * MANDATORY — credit can only be granted for money that moved somewhere
   * real. One of TRANSACTION_PLATFORM_OPTIONS' values.
   */
  transactionPlatform: string
  /**
   * MANDATORY for every platform EXCEPT OUT_OF_POCKET_PLATFORM — the
   * real-world reference for that payment (a Whop charge ID, a wire
   * confirmation number, a PayPal transaction ID, …). Pass '' when
   * transactionPlatform is OUT_OF_POCKET_PLATFORM; the worker/DB both treat
   * an empty string as "no external transaction" for that one platform.
   */
  transactionId: string
  /** Never shown to the brand. */
  internalNote?: string
  /**
   * ONE key per intended grant, reused across retries of that grant. A replay
   * returns the original result and moves no money; a genuinely new grant to the
   * same brand for the same amount needs its own key.
   */
  idempotencyKey: string
}

export async function grantBrandCredit(input: GrantBrandCreditInput): Promise<CreditGrantResult> {
  return workerAuthedFetch<CreditGrantResult>('/api/admin/brand-credit/grant', {
    method: 'POST',
    body: JSON.stringify({
      brand_id: input.brandId,
      amount_cents: input.amountCents,
      reason: input.reason,
      transaction_platform: input.transactionPlatform,
      transaction_id: input.transactionId,
      internal_note: input.internalNote || undefined,
      idempotency_key: input.idempotencyKey,
    }),
  })
}

// ── Formatting ──────────────────────────────────────────────────────────────

/**
 * Dollars typed by a human → whole cents. Rejects anything that isn't a clean
 * money amount rather than silently rounding it: on the one screen that mints
 * money, "12.345" is a typo, not an instruction.
 */
export function parseDollarsToCents(input: string): { cents: number; error: string | null } {
  const raw = input.trim().replace(/[$,\s]/g, '')
  if (!raw) return { cents: 0, error: null }
  if (!/^\d+(\.\d{1,2})?$/.test(raw)) {
    return { cents: 0, error: 'Enter a dollar amount like 250 or 250.50' }
  }
  const cents = Math.round(parseFloat(raw) * 100)
  if (!Number.isFinite(cents) || cents <= 0) return { cents: 0, error: 'Enter an amount greater than zero' }
  return { cents, error: null }
}

/** Human label for a grant status, matching the tone of the log column. */
export const GRANT_STATUS_LABEL: Record<CreditGrantStatus, string> = {
  applied: 'Applied',
  denied: 'Refused',
  failed: 'Failed',
}

// ============================================================================
// Admin-initiated brand credit DEDUCTION — the removal half
// ============================================================================
// Mirrors everything above, for the opposite direction. See
// creator-app/supabase/migrations/20260814000000_admin_credit_deductions.sql —
// this is a SEPARATE permission ('credits.deduct'), separate per-admin caps,
// separate rolling-24h counter, and a separate append-only log
// (brand_credit_deductions). Nothing above this point was changed to add it;
// an admin who holds 'credits.grant' does not automatically hold
// 'credits.deduct'.
// ============================================================================

export interface CreditDeductLimits {
  min_deduct_cents: number
  daily_limit_cents: number
  deducted_24h_cents: number
  remaining_24h_cents: number
}

export interface CreditDeductAdmin {
  user_id: string
  email: string
  display_name: string | null
  role: string
  role_label: string | null
  is_active: boolean
}

export interface CreditDeductBrand {
  id: string
  brand_name: string | null
  contact_email: string | null
  plan_tier: string | null
  balance_cents: number
  held_cents: number
  /** The hard ceiling on any deduction — balance minus held. */
  available_cents: number
  lifetime_deducted_cents: number
  deduction_count: number
  last_deducted_at: string | null
  /** The acting admin belongs to this brand's team — the DB refuses the deduction. */
  is_member_of_brand: boolean
}

export interface CreditDeductContext {
  /** null = this admin is not on the credit roster at all. */
  admin: CreditDeductAdmin | null
  is_auth_admin: boolean
  permissions: string[]
  can_deduct: boolean
  /** Why the deduct form is unavailable. null when can_deduct. */
  blocked_reason: string | null
  limits: CreditDeductLimits
  /** Same list as CreditGrantContext's — one shared allowlist for both forms. */
  transaction_platforms: string[]
  /** Present only when a brand_id was passed. */
  brand: CreditDeductBrand | null
}

export interface CreditDeductResult {
  deduction_id: string
  ledger_id: string
  brand_id: string
  brand_name: string | null
  deducted_cents: number
  balance_before_cents: number
  balance_cents: number
  deducted_by: string
  deducted_24h_cents: number
  daily_limit_cents: number
  transaction_platform: string | null
  transaction_id: string | null
  /** true = this exact deduction already landed; no new debit was created. */
  replayed: boolean
}

export type CreditDeductStatus = 'applied' | 'denied' | 'failed'

export interface CreditDeductionRow {
  id: string
  brand_id: string
  brand_name: string | null
  amount_cents: number
  reason: string
  /** Optional on a deduction — not every deduction reconciles one specific
   *  external payment. */
  transaction_platform: string | null
  transaction_id: string | null
  internal_note: string | null
  // Column names match brand_credit_transactions exactly (shared table with
  // grants — see fetchCreditDeductionLog's direction='deduct' filter).
  admin_user_id: string | null
  admin_email: string
  admin_role: string | null
  status: CreditDeductStatus
  error_message: string | null
  ledger_entry_id: string | null
  idempotency_key: string
  balance_before_cents: number | null
  balance_after_cents: number | null
  request_ip: string | null
  user_agent: string | null
  metadata: Record<string, unknown> | null
  reversed_at: string | null
  reversed_by: string | null
  created_at: string
}

export interface CreditDeductStats {
  window_days: number
  total_deducted_cents: number
  total_deductions: number
  brands_affected: number
  window_deducted_cents: number
  window_deductions: number
  /** Refused attempts in the window — a run of these is worth looking at. */
  window_refused: number
  reversed_cents: number
}

// ── Reads ───────────────────────────────────────────────────────────────────

/**
 * What this admin may DEDUCT, and (with a brand) that brand's live spendable
 * balance. Resolves the actor from the session's JWT server-side, same as
 * fetchCreditGrantContext — there is no way to ask about somebody else's
 * permissions, and this is a completely independent permission check from the
 * grant context (a 'credits.grant'-only admin gets can_deduct: false here).
 */
export async function fetchCreditDeductContext(brandId?: string | null): Promise<CreditDeductContext> {
  const { data, error } = await supabase.rpc('admin_my_credit_deduct_context', {
    p_brand_id: brandId ?? null,
  })
  if (error) throw error
  return data as CreditDeductContext
}

export async function fetchCreditDeductStats(days = 30): Promise<CreditDeductStats> {
  const { data, error } = await supabase.rpc('admin_credit_deduct_stats', { p_days: days })
  if (error) throw error
  return data as CreditDeductStats
}

export interface CreditDeductLogParams {
  page: number
  pageSize: number
  search?: string
  status?: CreditDeductStatus | 'all'
  brandId?: string | null
  adminEmail?: string | null
}

/**
 * The deduction log, newest first, with an exact total for pagination.
 * Shared table with fetchCreditGrantLog (brand_credit_transactions),
 * scoped to direction='deduct'.
 */
export async function fetchCreditDeductionLog(
  params: CreditDeductLogParams
): Promise<{ rows: CreditDeductionRow[]; total: number }> {
  const from = (params.page - 1) * params.pageSize
  const to = from + params.pageSize - 1

  let query = supabase
    .from('brand_credit_transactions')
    .select('*', { count: 'exact' })
    .eq('direction', 'deduct')
    .order('created_at', { ascending: false })
    .range(from, to)

  if (params.status && params.status !== 'all') query = query.eq('status', params.status)
  if (params.brandId) query = query.eq('brand_id', params.brandId)
  if (params.adminEmail) query = query.eq('admin_email', params.adminEmail)

  const search = params.search?.trim()
  if (search) {
    const escaped = search.replace(/[%,()]/g, '')
    query = query.or(
      `brand_name.ilike.%${escaped}%,admin_email.ilike.%${escaped}%,reason.ilike.%${escaped}%`
    )
  }

  const { data, error, count } = await query
  if (error) throw error
  return { rows: (data ?? []) as CreditDeductionRow[], total: count ?? 0 }
}

/** Distinct deducting admins, for the log's "deducted by" filter. */
export async function fetchDeductingAdmins(): Promise<string[]> {
  const { data, error } = await supabase
    .from('brand_credit_transactions')
    .select('admin_email')
    .eq('direction', 'deduct')
    .order('admin_email')
  if (error) throw error
  return Array.from(new Set((data ?? []).map((r: any) => r.admin_email).filter(Boolean)))
}

// ── Write ───────────────────────────────────────────────────────────────────

export interface DeductBrandCreditInput {
  brandId: string
  amountCents: number
  /** Shown to the brand on their wallet history. Mandatory, min 3 chars. */
  reason: string
  /**
   * OPTIONAL — unlike a grant, a deduction isn't always tied to one external
   * payment (a balance correction has no transaction to point at). When
   * given, must be one of TRANSACTION_PLATFORM_OPTIONS' values.
   */
  transactionPlatform?: string
  /** OPTIONAL — the real-world reference this deduction reconciles, if any. */
  transactionId?: string
  /** Never shown to the brand. */
  internalNote?: string
  /**
   * ONE key per intended deduction, reused across retries of that deduction.
   * A replay returns the original result and moves no money.
   */
  idempotencyKey: string
}

export async function deductBrandCredit(input: DeductBrandCreditInput): Promise<CreditDeductResult> {
  return workerAuthedFetch<CreditDeductResult>('/api/admin/brand-credit/deduct', {
    method: 'POST',
    body: JSON.stringify({
      brand_id: input.brandId,
      amount_cents: input.amountCents,
      reason: input.reason,
      transaction_platform: input.transactionPlatform || undefined,
      transaction_id: input.transactionId || undefined,
      internal_note: input.internalNote || undefined,
      idempotency_key: input.idempotencyKey,
    }),
  })
}

/** Human label for a deduction status — identical wording to GRANT_STATUS_LABEL
 *  but kept as its own export so the two systems never share a constant that
 *  one side's future change could silently affect the other. */
export const DEDUCT_STATUS_LABEL: Record<CreditDeductStatus, string> = {
  applied: 'Applied',
  denied: 'Refused',
  failed: 'Failed',
}

// ── Auto refunds ─────────────────────────────────────────────────────────────
// Read-only, like Purchases above — nobody clicks a button to create these.
// Plain frontend query straight against cr_credit_ledger (PostgREST embedded
// joins for brand/hire/campaign/creator context) rather than an RPC — see
// creator-app/supabase/migrations/20260910000003_admin_auto_refund_ledger.sql
// for the two RLS policies that make this readable to an admin session at all
// (cr_credit_ledger had no admin policy; brand_configs had none either).
//
// "Auto refunded" here = exactly one thing: created_by = 'system:deadline_cron'
// — the nightly cron crediting a brand back in full when a hire's deadline
// passed with ZERO videos delivered (cronAutoRefundZeroDelivery,
// Brand-Dashboard/worker/src/hiring.ts). That's the one row shape this tab
// filters to; nothing else in cr_credit_ledger qualifies. An admin-clicked
// refund lands in the SAME table under the SAME reason='hire_refund', but
// created_by is the admin's own email instead — that's what this filter
// excludes it on.

export interface AutoRefundRow {
  ledger_id: string
  brand_id: string
  brand_name: string | null
  amount_cents: number
  reason: string
  notes: string | null
  hire_id: string | null
  hire_status: string | null
  campaign_id: string | null
  campaign_name: string | null
  product_name: string | null
  creator_id: string | null
  creator_name: string | null
  creator_tiktok_username: string | null
  videos_delivered: number | null
  videos_required: number | null
  balance_after_cents: number | null
  posted_at: string | null
  created_at: string
}

export interface AutoRefundStats {
  /** Every automatic refund ever handed back, in cents. */
  total_refunded_cents: number
  total_refunds: number
  /** Distinct brands that have had at least one, all time. */
  brands_refunded: number
  /** Start of the calendar month the figures below cover, as the DATABASE
   *  clock sees it (UTC). Rendered so the tile can never imply a boundary it
   *  doesn't actually have. */
  month_start: string
  month_refunded_cents: number
  month_refunds: number
  month_brands: number
  /** Newest refund of any month, or null when there has never been one — lets
   *  a quiet month read as "last one was in March" rather than a bare zero. */
  last_refund_at: string | null
}

/**
 * Headline numbers for the auto-refund tab.
 *
 * Aggregated in SQL rather than over fetchAutoRefundLog's rows: PostgREST caps
 * a response at 1000 rows, so a client-side sum would quietly stop being a
 * total once the platform passes that many refunds. The function applies the
 * SAME created_by filter this file's fetchAutoRefundLog does, so the count on
 * the tile and the count the list pages through cannot disagree.
 */
export async function fetchAutoRefundStats(): Promise<AutoRefundStats> {
  const { data, error } = await supabase.rpc('admin_credit_auto_refund_stats')
  if (error) throw error
  return data as AutoRefundStats
}

export interface AutoRefundLogParams {
  page: number
  pageSize: number
  search?: string
}

/** Shape of one row as PostgREST returns it with the embeds below — internal,
 *  mapped into the flat AutoRefundRow the UI actually uses. */
interface RawAutoRefundRow {
  id: string
  brand_id: string
  amount_cents: number
  reason: string
  created_by: string
  notes: string | null
  related_hire_id: string | null
  balance_after_cents: number | null
  posted_at: string | null
  created_at: string
  metadata: Record<string, unknown> | null
  brand_configs: { brand_name: string | null } | null
  cr_hires: {
    status: string | null
    campaign_id: string | null
    creator_id: string | null
    cr_campaigns: { name: string | null; product_name: string | null } | null
    roster_creators: { name: string | null; tiktok_username: string | null } | null
  } | null
}

export async function fetchAutoRefundLog(
  params: AutoRefundLogParams
): Promise<{ rows: AutoRefundRow[]; total: number }> {
  const from = (params.page - 1) * params.pageSize
  const to = from + params.pageSize - 1

  let query = supabase
    .from('cr_credit_ledger')
    .select(
      `
      id, brand_id, amount_cents, reason, created_by, notes, related_hire_id,
      balance_after_cents, posted_at, created_at, metadata,
      brand_configs!inner ( brand_name ),
      cr_hires (
        status, campaign_id, creator_id,
        cr_campaigns ( name, product_name ),
        roster_creators ( name, tiktok_username )
      )
    `,
      { count: 'exact' }
    )
    .eq('created_by', 'system:deadline_cron')
    .order('created_at', { ascending: false })
    .range(from, to)

  // Brand name only — the useful common case ("find X's refunds"). Filtering
  // an embedded resource needs the `!inner` join hint above to actually
  // constrain the parent rows; brand_id is NOT NULL so that join never drops
  // a real row when no search term is active.
  const search = params.search?.trim()
  if (search) {
    const escaped = search.replace(/[%,()]/g, '')
    query = query.ilike('brand_configs.brand_name', `%${escaped}%`)
  }

  const { data, error, count } = await query
  if (error) throw error

  const rows = ((data ?? []) as unknown as RawAutoRefundRow[]).map((r): AutoRefundRow => ({
    ledger_id: r.id,
    brand_id: r.brand_id,
    brand_name: r.brand_configs?.brand_name ?? null,
    amount_cents: r.amount_cents,
    reason: r.reason,
    notes: r.notes,
    hire_id: r.related_hire_id,
    hire_status: r.cr_hires?.status ?? null,
    campaign_id: r.cr_hires?.campaign_id ?? null,
    campaign_name: r.cr_hires?.cr_campaigns?.name ?? null,
    product_name: r.cr_hires?.cr_campaigns?.product_name ?? null,
    creator_id: r.cr_hires?.creator_id ?? null,
    creator_name: r.cr_hires?.roster_creators?.name ?? null,
    creator_tiktok_username: r.cr_hires?.roster_creators?.tiktok_username ?? null,
    videos_delivered: typeof r.metadata?.videos_delivered === 'number' ? r.metadata.videos_delivered as number : null,
    videos_required: typeof r.metadata?.videos_required === 'number' ? r.metadata.videos_required as number : null,
    balance_after_cents: r.balance_after_cents,
    posted_at: r.posted_at,
    created_at: r.created_at,
  }))

  return { rows, total: count ?? 0 }
}
